import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CtaButton, Eyebrow, PageMark, SceneStage } from "./GhxpUi";
import { CONCEPT_CLIP, CONCEPT_ROOM } from "./ghxpAssets";
import useSceneVideo from "./useSceneVideo";

const FINE_POINTER = window.matchMedia("(hover: hover) and (pointer: fine)");
// A sala em vídeo é o segundo vídeo tocando junto com a multidão nas letras: só fica se a página seguir lisa.
const ROOM_MIN_FPS = 40;
let roomVideoOk = true; // decidido uma vez por visita à página
// Altura de maiúscula da Inter e respiro em volta da palavra, em unidades do SVG (font-size 100).
const CAP = 72.7;
const PAD = 8;
const TEXT_STYLE = { fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 100, letterSpacing: "-0.01em" };

// "Mais longe, <strong>juntos</strong>." -> { lead: "Mais longe,", word: "JUNTOS." }
function splitTagline(str) {
  const match = String(str).match(/^(.*?)<strong>(.*?)<\/strong>(.*)$/);
  return match ? { lead: match[1].trim(), word: `${match[2]}${match[3]}`.trim().toUpperCase() } : { lead: str, word: "" };
}

// Mede a palavra no próprio SVG (getBBox ignora o rotate do texto) e devolve o recorte justo, a área da névoa
// e a proporção da caixa. Mede de novo quando a Inter 700 carrega e quando o slide fica visível
// (fora da tela, content-visibility: hidden pode devolver uma caixa vazia).
function useWordGeometry(textRef, word, spine, visible) {
  const [geo, setGeo] = useState(null);

  useLayoutEffect(() => {
    let alive = true;
    const measure = () => {
      if (!alive || !textRef.current) return;
      const box = textRef.current.getBBox();
      if (!box.width) return;
      const x = box.x - PAD;
      const w = box.width + PAD * 2;
      const y = -CAP - PAD;
      const h = CAP + PAD * 2;
      const next = spine
        ? { tight: [y, -x - w, h, w], haze: [y - h * 0.45, -x - w - w * 0.04, h * 1.9, w * 1.08], aspect: h / w }
        : { tight: [x, y, w, h], haze: [x - w * 0.06, y - h * 0.3, w * 1.12, h * 1.6], aspect: w / h };
      // Borda esfumada da máscara navy: a parte opaca cobre a caixa da palavra (+ margem) e some até a borda
      // da névoa (desfoque gaussiano, 2,33 desvios de cada lado = ~99%), para o salão atrás não formar uma caixa.
      const [tx, ty, tw, th] = next.tight;
      const band = Math.min(tx - next.haze[0], ty - next.haze[1]);
      const sd = band / 4.66;
      const m = sd * 2.33;
      next.feather = { sd, rect: [tx - m, ty - m, tw + m * 2, th + m * 2] };
      setGeo(next);
    };
    measure();
    document.fonts?.load("700 100px Inter").then(measure, () => {});
    return () => {
      alive = false;
    };
  }, [textRef, word, spine, visible]);

  return geo;
}

// Zona limpa: centro e raios de uma elipse em volta da palavra (caixa de layout, sem a inclinação), em px do slide,
// como variáveis CSS no <section>. A sombra do palco escurece o salão nessa elipse, então a máscara navy das letras
// nunca fica sobre algo mais claro que o navy. Mede de novo quando a palavra ou o texto mudam de tamanho e ao rolar.
function useCleanZone(contentRef, wordRef, copyRef, enabled, layoutKey) {
  useLayoutEffect(() => {
    const content = contentRef.current;
    const wordBox = wordRef.current;
    const slide = content && content.parentElement;
    if (!slide || !wordBox) return undefined;
    const names = ["--hole-x", "--hole-y", "--hole-rx", "--hole-ry"];
    if (!enabled) {
      names.forEach((name) => slide.style.removeProperty(name));
      return undefined;
    }
    let frame = 0;
    const place = () => {
      frame = 0;
      const w = wordBox.offsetWidth;
      const h = wordBox.offsetHeight;
      if (!w) return;
      const x = content.offsetLeft + wordBox.offsetLeft + w / 2;
      const y = content.offsetTop - content.scrollTop + wordBox.offsetTop + h / 2;
      [x, y, w * 0.8, h * 1.6].forEach((value, i) => slide.style.setProperty(names[i], `${Math.round(value)}px`));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(place);
    };
    place();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(schedule) : null;
    observer?.observe(wordBox);
    if (copyRef.current) observer?.observe(copyRef.current);
    content.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      observer?.disconnect();
      content.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.cancelAnimationFrame(frame);
    };
  }, [contentRef, wordRef, copyRef, enabled, layoutKey]);
}

// Sala em vídeo (desktop com mouse): 2,5 s depois de assentar, mede 1 s de quadros. Abaixo de 40 fps (máquina fraca,
// modo economia de energia, renderização por software) a sala fica na foto do mesmo quadro pelo resto da visita.
function useRoomVideo(wanted, settled) {
  const [ok, setOk] = useState(roomVideoOk);

  useEffect(() => {
    if (!wanted || !ok || !settled) return undefined;
    let raf = 0;
    const timer = window.setTimeout(() => {
      if (document.hidden) return;
      let frames = 0;
      let start = 0;
      let last = 0;
      const tick = (now) => {
        if (start && now - last > 250) return; // aba escondida ou um engasgo isolado: não decide nada
        if (!start) start = now;
        last = now;
        frames += 1;
        if (now - start < 1000) {
          raf = window.requestAnimationFrame(tick);
        } else if (((frames - 1) * 1000) / (now - start) < ROOM_MIN_FPS) {
          roomVideoOk = false;
          setOk(false);
        }
      };
      raf = window.requestAnimationFrame(tick);
    }, 2500);
    return () => {
      window.clearTimeout(timer);
      window.cancelAnimationFrame(raf);
    };
  }, [wanted, ok, settled]);

  return wanted && ok;
}

// Cena 1 — "Mais longe, juntos.": a frase vira a imagem. "MAIS LONGE," se afasta letra a letra até o espaçamento
// da marca; "JUNTOS." em tamanho de cartaz tem as letras vazadas sobre a multidão do evento real (navy e laranja).
// O contorno laranja se desenha, uma linha de luz varre as letras e revela as pessoas; com o mouse a palavra inclina,
// a multidão se move ao contrário e uma luz segue o cursor só por dentro das letras. No celular, a palavra vira
// uma lombada vertical à direita. Abaixo da palavra, só o manifesto (com o traço laranja) e o CTA.
// Fundo: o salão do jantar à noite, desfocado no próprio arquivo, no mesmo palco das outras cenas (inclina com o
// mouse, luz do cursor, sombra navy do lado do texto, a seta dupla que se desenha); em volta da palavra, a zona limpa.
function GhxpConcept({ onStart, near, warm, active, settled, visible, lowPower, spine }) {
  const { t } = useTranslation();
  const tagline = t("ghxp_tagline");
  const { lead, word } = splitTagline(tagline);
  const rimRef = useRef(null);
  const wordRef = useRef(null);
  const windowRef = useRef(null);
  const contentRef = useRef(null);
  const copyRef = useRef(null);
  const frameRef = useRef(0);
  const geo = useWordGeometry(rimRef, word, spine, visible);
  const { videoRef } = useSceneVideo({ active, settled, warm, enabled: !lowPower, variant: spine });
  // Sala em vídeo só no layout largo com mouse; no toque, na lombada e no baixo consumo, a foto do mesmo quadro.
  const roomVideo = useRoomVideo(!lowPower && !spine && FINE_POINTER.matches, settled);
  const room = useSceneVideo({ active, settled, warm, enabled: roomVideo, variant: spine });
  useCleanZone(contentRef, wordRef, copyRef, !spine && visible, `${tagline}|${geo ? geo.aspect : 0}`);

  const clip = spine ? CONCEPT_CLIP.tall : CONCEPT_CLIP.wide;
  const poster = spine ? CONCEPT_CLIP.posterTall : CONCEPT_CLIP.poster;
  const rotate = spine ? "rotate(-90)" : undefined;
  const [hx, hy, hw, hh] = geo ? geo.haze : [0, 0, 1, 1];
  const [fx, fy, fw, fh] = geo ? geo.feather.rect : [0, 0, 1, 1];

  // Luz que segue o cursor só por dentro das letras (uma atualização por quadro).
  const onMouseMove = (event) => {
    if (spine || lowPower || !FINE_POINTER.matches || frameRef.current) return;
    const { clientX, clientY } = event;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = 0;
      const frame = windowRef.current;
      const wordBox = wordRef.current;
      if (!frame || !wordBox) return;
      const rect = frame.getBoundingClientRect();
      wordBox.style.setProperty("--lx", `${Math.round(clientX - rect.left)}px`);
      wordBox.style.setProperty("--ly", `${Math.round(clientY - rect.top)}px`);
      wordBox.classList.add("is-lit");
    });
  };

  // Mesma geometria na máscara e no contorno: o texto fica idêntico nas duas camadas.
  const svgText = (props) => (
    <text x="0" y="0" style={TEXT_STYLE} transform={rotate} {...props}>
      {word}
    </text>
  );

  // Cada palavra do "MAIS LONGE," é um bloco que não quebra; o espaço fica entre os blocos.
  let charIndex = 0;
  const leadWords = lead.split(/\s+/).filter(Boolean);
  const leadCount = leadWords.join("").length;

  return (
    <>
      <SceneStage shade="concept" className={`ghxpConcept__stage ${spine ? "is-spine" : ""}`}>
        <div className="ghxpConcept__push">
          {near && (
            <img
              className="ghxpStage__poster"
              src={spine ? CONCEPT_ROOM.posterTall : CONCEPT_ROOM.poster}
              width={spine ? 360 : 640}
              height={spine ? 640 : 360}
              alt=""
              decoding="async"
            />
          )}
          {near && roomVideo && (
            <video
              ref={room.videoRef}
              className={`ghxpStage__video ${room.playing ? "is-playing" : ""}`}
              src={CONCEPT_ROOM.wide}
              muted
              loop
              playsInline
              preload={warm ? "auto" : "none"}
              onPlaying={room.onPlaying}
            />
          )}
        </div>
      </SceneStage>

      <div
        ref={contentRef}
        className={`ghxpConcept__content ${spine ? "is-spine" : ""}`}
        data-scrollable="y"
        onMouseMove={onMouseMove}
        style={geo ? { "--aspect": geo.aspect.toFixed(4) } : undefined}
      >
        <Eyebrow className="ghxpReveal">{t("ghxp_concept_eyebrow")}</Eyebrow>

        <h2 className="ghxpConcept__title">
          <span className="ghxpSrOnly">{tagline.replace(/<[^>]+>/g, "")}</span>
          <span className="ghxpConcept__lead" aria-hidden="true">
            {leadWords.map((leadWord, w) => (
              <React.Fragment key={`${leadWord}-${w}`}>
                {w > 0 && " "}
                <span className="ghxpConcept__leadWord">
                  {[...leadWord].map((ch) => {
                    const c = charIndex;
                    charIndex += 1;
                    return (
                      <span key={c} className="ghxpConcept__char" style={{ "--c": c, "--n": leadCount }}>
                        {ch}
                      </span>
                    );
                  })}
                </span>
              </React.Fragment>
            ))}
          </span>

          <span ref={wordRef} className={`ghxpWord ${geo ? "is-ready" : ""}`} aria-hidden="true">
            <span ref={windowRef} className="ghxpWord__window">
              {near &&
                (lowPower ? (
                  <img className="ghxpWord__video" src={poster} alt="" decoding="async" />
                ) : (
                  <video
                    ref={videoRef}
                    key={spine ? "tall" : "wide"}
                    className="ghxpWord__video"
                    src={clip}
                    poster={poster}
                    muted
                    loop
                    playsInline
                    preload={warm ? "auto" : "none"}
                  />
                ))}
              <i className="ghxpWord__light" />
              <i className="ghxpWord__curtain" />
            </span>

            {geo && (
              <svg className="ghxpWord__knock" viewBox={`${hx} ${hy} ${hw} ${hh}`} preserveAspectRatio="none" focusable="false">
                <defs>
                  <radialGradient id="ghxpWordHaze" cx="50%" cy="50%" r="50%">
                    <stop offset="0" stopColor="#0B1628" stopOpacity="0.9" />
                    <stop offset="0.45" stopColor="#0B1628" stopOpacity="0.96" />
                    <stop offset="0.65" stopColor="#0B1628" stopOpacity="1" />
                  </radialGradient>
                  <filter id="ghxpWordFeather" filterUnits="userSpaceOnUse" x={hx} y={hy} width={hw} height={hh}>
                    <feGaussianBlur stdDeviation={geo.feather.sd.toFixed(2)} />
                  </filter>
                  <mask id="ghxpWordHoles" maskUnits="userSpaceOnUse" x={hx} y={hy} width={hw} height={hh}>
                    <rect x={fx} y={fy} width={fw} height={fh} fill="#fff" filter="url(#ghxpWordFeather)" />
                    {svgText({ fill: "#000" })}
                  </mask>
                </defs>
                <rect x={hx} y={hy} width={hw} height={hh} fill="url(#ghxpWordHaze)" mask="url(#ghxpWordHoles)" />
              </svg>
            )}

            <svg className="ghxpWord__rim" viewBox={geo ? geo.tight.join(" ") : "0 0 1 1"} focusable="false">
              {svgText({ ref: rimRef, className: "ghxpWord__rimText" })}
            </svg>
          </span>
        </h2>

        <div ref={copyRef} className="ghxpConcept__copy">
          <p className="ghxpConcept__manifesto ghxpReveal">
            <Trans i18nKey="ghxp_concept_text_2" />
          </p>
          <div className="ghxpConcept__actions ghxpReveal">
            <CtaButton onClick={onStart}>{t("ghxp_cta")}</CtaButton>
          </div>
        </div>
      </div>

      <PageMark />
    </>
  );
}

export default GhxpConcept;
